import React, { useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  EmptyState,
  EmptyStateBody,
  Label,
  LabelGroup,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { CubesIcon, PlusCircleIcon } from '@patternfly/react-icons';
import { useAdminVerdaVolumeProducts } from '../../../hooks/useAdminVerda';
import type { VerdaVolumeProductLink } from '../../../types/models/Verda';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import { formatCurrencyAmount, formatUnitPrice } from '../../../utils/money';
import { VerdaErrorAlert, VerdaRefreshButton } from './VerdaTabParts';
import VerdaCreateStorageProductsModal from './VerdaCreateStorageProductsModal';

/** The storage type whose link cost prices the fixed 100 GB server disk in customer budgets. */
const DISK_STORAGE_TYPE = 'block_nvme';

const ProductCell: React.FunctionComponent<{ link: VerdaVolumeProductLink }> = ({ link }) => {
  if (link.product === null) {
    return <span className="pf-v6-u-text-color-subtle">Product missing</span>;
  }
  const { product } = link;
  return (
    <>
      <div>{product.label}</div>
      <div className="pf-v6-u-font-family-monospace pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
        {product.product_key}
      </div>
      <LabelGroup isCompact className="pf-v6-u-mt-xs" aria-label={`Status of ${product.product_key}`}>
        {product.active ? (
          <Label isCompact color="green">
            Active
          </Label>
        ) : (
          <Label isCompact color="grey">
            Inactive
          </Label>
        )}
        {!product.tenant_visible && (
          <Label isCompact variant="outline">
            Not on the pricing page
          </Label>
        )}
      </LabelGroup>
    </>
  );
};

/**
 * Admin › Verda › Storage products — the monthly per-GB catalog products created from Verda volume types
 * (docs/api-types/admin-verda.md "Storage products (GPU budgets)"). They only price supplementary storage
 * in customer GPU budgets, and the NVMe link's stored cost also prices the fixed 100 GB server disk
 * there: until it exists in an environment, every customer budget shows server prices as unavailable.
 *
 * A Manager-side read (no Verda call), fetched once when the tab opens (`mountOnEnter`), then on
 * Refresh and after a create. "Create missing storage products" reads Verda's live prices and writes
 * catalog rows — only on an explicit click, through a confirmation.
 *
 * A compact table with no search and no pagination — an explicit opt-out from the list-table rule
 * (Bruno, 2026-09-29): there are three mapped volume types.
 */
const VerdaStorageProductsTab: React.FunctionComponent<{ isStaff: boolean }> = ({ isStaff }) => {
  const { products, loading, error, refetch } = useAdminVerdaVolumeProducts(isStaff);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createRun, setCreateRun] = useState(0);

  const openCreate = () => {
    setCreateRun((n) => n + 1);
    setIsCreateOpen(true);
  };

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="storage products" message={error} />;
    }
    // No data and no error = loading, including the render before the lazy fetch starts.
    if (products === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the Verda storage products" />
        </Bullseye>
      );
    }
    const hasDiskProduct = products.some((link) => link.storage_type === DISK_STORAGE_TYPE);
    return (
      <>
        {!hasDiskProduct && (
          <Alert variant="warning" isInline title="The NVMe storage product is missing" className="pf-v6-u-mb-md">
            Customers&apos; budgets show server prices as unavailable until the NVMe storage product exists (its cost
            prices the 100 GB server disk).
          </Alert>
        )}
        {products.length === 0 ? (
          <EmptyState titleText="No storage products yet" icon={CubesIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>
              Run “Create missing storage products” once in this environment: customers cannot add supplementary storage
              to their budgets until then.
            </EmptyStateBody>
          </EmptyState>
        ) : (
          <Table aria-label="Verda storage products" variant="compact">
            <Thead>
              <Tr>
                <Th>Volume type</Th>
                <Th>Storage type</Th>
                <Th>Product</Th>
                <Th modifier="nowrap">Verda cost € / GB / month</Th>
                <Th modifier="nowrap">Default price € / GB / month</Th>
                <Th>Created</Th>
              </Tr>
            </Thead>
            <Tbody>
              {products.map((link) => (
                <Tr key={link.volume_type}>
                  <Td dataLabel="Volume type">
                    <span className="pf-v6-u-font-family-monospace">{link.volume_type}</span>
                  </Td>
                  <Td dataLabel="Storage type">
                    <span className="pf-v6-u-font-family-monospace">{link.storage_type}</span>
                  </Td>
                  <Td dataLabel="Product">
                    <ProductCell link={link} />
                  </Td>
                  <Td dataLabel="Verda cost € / GB / month" modifier="nowrap">
                    {formatCurrencyAmount(link.cost_per_gb_month, link.cost_currency)}
                  </Td>
                  <Td dataLabel="Default price € / GB / month" modifier="nowrap">
                    {link.product !== null && link.product.default_unit_price !== null
                      ? formatUnitPrice(link.product.default_unit_price)
                      : '—'}
                  </Td>
                  <Td dataLabel="Created" modifier="nowrap">
                    {formatWallClockDateTime(link.created_at)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <Button variant="primary" icon={<PlusCircleIcon />} onClick={openCreate}>
              Create missing storage products
            </Button>
          </ToolbarItem>
          <ToolbarItem>
            <VerdaRefreshButton loading={loading} onRefresh={refetch} what="storage products" />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
      {isCreateOpen && (
        <VerdaCreateStorageProductsModal
          key={createRun}
          onClose={() => setIsCreateOpen(false)}
          onCreated={() => void refetch()}
        />
      )}
    </>
  );
};

export default VerdaStorageProductsTab;
